<template>
  <div class="user-avatar-container">
    <img v-if="user && userPicture" alt="avatar" :class="'size-' + size" :src="userPicture" />
    <div v-else class="image-placeholder" :class="[`background-color-${colors[colorIndex]}`, 'size-' + size]">
      <span>{{ initials }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator'
import UserAvatarMixin from '@/modules/global/components/User/UserAvatarMixin'

@Component({
  mixins: [UserAvatarMixin]
})
export default class UserAvatar extends UserAvatarMixin {
  private colors = [
    'avatar-orange',
    'avatar-purple',
    'avatar-green',
    'avatar-yellow',
    'avatar-red',
    'powder',
    'lavender',
    'dusk',
    'harvest',
    'rose',
    'sky',
    'pistachio',
    'taupe',
    'silver',
    'scallop',
    'salvia'
  ]

  get userPicture(): string {
    return this.user.profilePictureUrl!!
  }

  get initials(): string {
    if (!this.user) {
      return this.avatarInitials ?? ''
    }

    if (this.size === 'xs' || this.size === 'xxs') {
      return (this.user.firstName || this.user.email).charAt(0)
    }

    if (this.user.firstName) {
      if (this.user.lastName) {
        return (this.user.firstName.charAt(0) + this.user.lastName.charAt(0)).toUpperCase()
      } else if (this.user.firstName.length > 1) {
        return (this.user.firstName.charAt(0) + this.user.firstName.charAt(1)).toUpperCase()
      }
    }

    return (this.user.email.charAt(0) + this.user.email.charAt(1)).toUpperCase()
  }

  get colorIndex(): number {
    const hash = this.hashCode(this.user?.email)
    return hash % this.colors.length
  }

  hashCode(s?: string): number {
    let hash = 0,
      i,
      chr
    if (!s || s.length === 0) return hash
    for (i = 0; i < s.length; i++) {
      chr = s.charCodeAt(i)
      hash = (hash << 5) - hash + chr
      hash |= 0
    }
    return Math.abs(hash)
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.user-avatar-container {
  display: flex;
  align-items: center;
  position: relative;
}

img[alt='avatar'] {
  border: 0 solid transparent;
  border-radius: $notch-radius-full;

  object-fit: cover;
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: $notch-color-neutral-grey-900;
  border-radius: $notch-radius-full;
  user-select: none;

  span {
    transform: translate(1px, 0px);
  }
}

.size-l {
  width: $notch-space-10x;
  height: $notch-space-10x;
  min-width: $notch-space-10x;
  min-height: $notch-space-10x;

  span {
    @include notch-text-overline-M;
  }
}

.size-m {
  width: $notch-space-8x;
  height: $notch-space-8x;
  min-width: $notch-space-8x;
  min-height: $notch-space-8x;

  span {
    @include notch-text-overline-M;
  }
}

.size-s {
  width: $notch-space-6x;
  height: $notch-space-6x;
  min-width: $notch-space-6x;
  min-height: $notch-space-6x;

  span {
    @include notch-text-overline-S;
  }
}

.size-xs {
  width: $notch-space-4x;
  height: $notch-space-4x;
  min-width: $notch-space-4x;
  min-height: $notch-space-4x;

  span {
    @include notch-text-overline-S;
    transform: translate(0, 0);
  }
}

.size-xxs {
  width: $notch-space-3x;
  height: $notch-space-3x;
  min-width: $notch-space-3x;
  min-height: $notch-space-3x;

  span {
    @include notch-text-overline-S;
    transform: translate(0.5px, 0);
  }
}

.size-xl {
  width: $notch-space-18x;
  height: $notch-space-18x;

  @include mobile {
    width: $notch-space-15x;
    height: $notch-space-15x;
  }

  span {
    @include notch-text-overline-M;
  }
}

.background-color-avatar-orange {
  background-color: $notch-color-avatar-orange;
}

.background-color-avatar-purple {
  background-color: $notch-color-avatar-purple;
}

.background-color-avatar-green {
  background-color: $notch-color-avatar-green;
}

.background-color-avatar-yellow {
  background-color: $notch-color-avatar-yellow;
}

.background-color-avatar-red {
  background-color: $notch-color-avatar-red;
}

.background-color-powder {
  background-color: $notch-color-brand-powder;
}

.background-color-lavender {
  background-color: $notch-color-brand-lavender;
}

.background-color-dusk {
  background-color: $notch-color-brand-dusk;
}

.background-color-harvest {
  background-color: $notch-color-brand-harvest;
}

.background-color-rose {
  background-color: $notch-color-brand-rose;
}

.background-color-sky {
  background-color: $notch-color-brand-sky;
}

.background-color-pistachio {
  background-color: $notch-color-brand-pistachio;
}

.background-color-taupe {
  background-color: $notch-color-brand-taupe;
}

.background-color-silver {
  background-color: $notch-color-brand-silver;
}

.background-color-scallop {
  background-color: $notch-color-brand-scallop;
}

.background-color-salvia {
  background-color: $notch-color-brand-salvia;
}
</style>
